import React, { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import has from 'lodash/has';
import styled from 'styled-components';
import { DisableWrapper } from '../Wrapper/DisableWrapper';
import Utils from '../../utils';
import ProductCover from '../ProductCover';
import SelectButton from '../SelectButton';
import { PreviewButton } from '../../preview/PreviewButton';
import ProductSeries from '../../product/ProductSeries';
import ProductAuthors from '../../product/ProductAuthors';
import ProductNarrators from '../../product/ProductNarrators';
import ProductGenres from '../../product/ProductGenres';
import ProductAge from '../../product/ProductAge';
import { ProductFormat } from '../../product/ProductFormat';
import ProductPublisher from '../../product/ProductPublisher';
import { ProductReleaseDate } from '../../product/ProductReleaseDate';
import ProductUsageConditions from '../../product/ProductUsageConditions';
import ProductLanguage from '../../product/ProductLanguage';
import { ListBadges } from '../BadgesV2';
import { CenteredListBadges } from '../BadgesV2/CenteredBadgeContainer';
import LibraryManagedProductGroupDropdown from '../LibraryManagedProductGroupDropdown';
import { PRODUCT_CLASS_CAMPAIGN } from '../../../store/constants';
import { AddProduct } from '../AddProduct/AddProduct';
import { Button, ButtonSize, ButtonTheme } from '../Button';
import { MATERIAL_ICON_SETTINGS, MATERIAL_ICON_TRASH } from '../Button/Icon';
import { ProductMetaData } from '../../../../models/Product';
import { ProductCopyStockDropdown } from '../../product/ProductCopyStock';
import MaterialIcon from '../MaterialIcon';
import SelectionListProductItemDropDown from '../SelectionListProductItemDropDown';
import CartPriceActionButton from '../../../shop/cart/CartPriceActionButton';
import MetadataUtils from '../../utils/utils-metadata';
import { Trans, useTranslation } from 'react-i18next';
import { showReserveWarning } from '../../product/ProductStatistics';
import { RedundantStatisticPopups } from '../../../shop/reserve/components/ReserveListItem';
import { Article } from '../../../../models/Article';
import { hasProductSelection } from '../../product-selection/ProductSelectionUtils';
import { concreteLoanFormatKeyToObject, concreteLoanFormatNameToKey } from '../../loanFormat/mappers';
import { ProductListItemProps } from '../ProductList';
import { useAddProductToGroup } from '../../../api/productGroups/useAddProductToGroup';

const StLibraryManagedProductGroupDropdown = styled(LibraryManagedProductGroupDropdown)`
    margin-top: 7px;
`;

const StCopyStocksWrapper = styled.div`
    color: red;
`;

function isProductDisabled(product: ProductMetaData & Article) {
    return !has(product, 'copyStockAmount') || product.copyStockAmount === 0;
}

const StMaterialIcon = styled(MaterialIcon)`
    font-size: 13px;
    vertical-align: top;
    margin-right: 5px;
`;

type Props = ProductListItemProps & {
    readonly showManagePopup: () => void;
    readonly onRemoveProductFromList?: () => Promise<void>;
};

const CollectionListItem: FC<Props> = ({
    actionButtons,
    isInPartnerContext,
    isProductSelectable,
    isProductSelected,
    loanFormat,
    onAddPrice,
    onProductImageClick,
    onRemoveProductFromList,
    onSelectProduct,
    product,
    productSelection,
    showManagePopup
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const { t } = useTranslation();
    const [showReserveInfoSplit, setShowReserveInfoSplit] = useState(false);
    const [showCopyStockSplit, setShowCopyStockSplit] = useState(false);
    const addProductToGroup = useAddProductToGroup(product);

    const copyStockSplitPopoverRef = useRef<HTMLDivElement>(null);

    const onShowDetails = () => setShowDetails(true);
    const hideDetails = () => setShowDetails(false);

    const loanFormatKey = loanFormat;
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const productLoanFormatObject = concreteLoanFormatKeyToObject(productLoanFormatKey);

    const packshotClasses = classNames('product-list-cell', 'packshot', 'catalogue', {
        over: showDetails
    });

    const productLink = !hasProductSelection(productSelection) ? `/products/${product.id}` : '#';

    useEffect(() => {
        setIsDisabled(isProductDisabled(product));
        return () => {};
    }, [product]);

    const { subTitle } = product;
    const combinedTitle = subTitle ? `${product.title} ${subTitle}` : product.title;

    //TODO: check if actionsButtons are deprecated
    const buildActions = () => {
        const isNoCampaignProduct = product.productClass !== PRODUCT_CLASS_CAMPAIGN;
        if (actionButtons) {
            return (
                <>
                    <div className="bt-line">{actionButtons}</div>
                    <AddProduct
                        product={product}
                        onAddPrice={onAddPrice}
                        onSelectPrice={async () => {}}
                        showSelectedPriceOnly={false}
                        allowSelection={true}
                    />
                    <SelectionListProductItemDropDown product={product} onAddPrice={onAddPrice} />
                    {MetadataUtils.isProductAvailableForSale(product) ? (
                        <CartPriceActionButton product={product} onAddPrice={onAddPrice} />
                    ) : null}
                </>
            );
        } else {
            return (
                <>
                    <div className="bt-line">
                        {onRemoveProductFromList ? (
                            <Button
                                key={`remove-${product.id}`}
                                text={t('remove')}
                                onClick={onRemoveProductFromList}
                                icon={MATERIAL_ICON_TRASH}
                                size={ButtonSize.SMALL}
                                theme={ButtonTheme.NEUTRAL}
                                fullWidth={true}
                                className="mb-sm"
                            />
                        ) : null}
                        <Button
                            text={t('manage')}
                            onClick={showManagePopup}
                            icon={MATERIAL_ICON_SETTINGS}
                            size={ButtonSize.SMALL}
                            theme={ButtonTheme.NEUTRAL}
                            fullWidth={true}
                        />
                        {isNoCampaignProduct ? (
                            <StLibraryManagedProductGroupDropdown onListClick={addProductToGroup} />
                        ) : null}
                    </div>
                    <AddProduct
                        product={product}
                        onAddPrice={onAddPrice}
                        onSelectPrice={async () => {}}
                        showSelectedPriceOnly={false}
                        allowSelection={true}
                    />
                </>
            );
        }
    };
    const reserveWarning = showReserveWarning(
        (product?.combinedReserveInfo?.ownReserveCount ?? 0) + (product?.combinedReserveInfo?.foreignReserveCount ?? 0),
        product?.copyStockAmount ?? 0
    );

    const ProductStatistics = ({ product }: { product: ProductMetaData & Article }) => {
        if (has(product, 'combinedReserveInfo')) {
            const reserveIconClasses = classNames('material-icons', 'small', 'fl-left', {
                'cold-red': reserveWarning
            });

            return (
                <div>
                    <div>
                        <div
                            style={{ marginBottom: '10px', display: 'inline-block' }}
                            onMouseEnter={() => setShowReserveInfoSplit(true)}
                            onMouseLeave={() => setShowReserveInfoSplit(false)}
                        >
                            <i className={reserveIconClasses}>person</i>
                            <Trans
                                i18nKey="components.listItem.reserves"
                                components={{
                                    1: <span className="fatdata" />
                                }}
                                values={{ reserves: product.reserveAmount }}
                            />
                            <RedundantStatisticPopups
                                product={product}
                                showReserveInfoSplit={showReserveInfoSplit}
                                isInPartnerContext={isInPartnerContext}
                                t={t}
                            />
                        </div>
                    </div>
                    <div>
                        {(product?.copyStockAmount ?? 0) <= 0 || !product.stockDetails ? (
                            <div title={t('components.productStatistics.copiesInYourLibrary')}>
                                <StMaterialIcon icon="content_copy" />
                                <Trans
                                    i18nKey="components.listItem.copies"
                                    components={{
                                        1: <span className="fatdata" />
                                    }}
                                    values={{ copies: 0 }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{ display: 'inline-block' }}
                                onMouseEnter={() => setShowCopyStockSplit(true)}
                                onMouseLeave={() => setShowCopyStockSplit(false)}
                            >
                                <span ref={copyStockSplitPopoverRef}>
                                    <StMaterialIcon icon="content_copy" />
                                </span>
                                <Trans
                                    i18nKey="components.listItem.copies"
                                    components={{
                                        1: <span className="fatdata" />
                                    }}
                                    values={{ copies: product.copyStockAmount }}
                                />
                                <ProductCopyStockDropdown
                                    loanFormat={productLoanFormatObject!}
                                    stockDetails={product.stockDetails}
                                    placement="bottom-start"
                                    visible={showCopyStockSplit}
                                    popoverRef={copyStockSplitPopoverRef}
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        } else {
            return (
                <div>
                    <div>
                        <div style={{ marginBottom: '10px', display: 'inline-block' }}>
                            <i className="material-icons small fl-left">person</i>
                            <Trans
                                i18nKey="components.listItem.reserves"
                                components={{
                                    1: <span className="fatdata" />
                                }}
                                values={{ reserves: 0 }}
                            />
                        </div>
                    </div>
                    <div>
                        <StCopyStocksWrapper style={{ display: 'inline-block' }}>
                            <StMaterialIcon icon="content_copy" />
                            <Trans
                                i18nKey="components.listItem.copies"
                                components={{
                                    1: <span className="fatdata">0</span>
                                }}
                                values={{ copies: 0 }}
                            />
                        </StCopyStocksWrapper>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="product-list-row cc-list-row">
            <DisableWrapper className={packshotClasses} isDisabled={isDisabled}>
                <Link
                    to={productLink}
                    onClick={e => {
                        if (onProductImageClick) {
                            e.preventDefault();
                            onProductImageClick();
                        }
                    }}
                >
                    <ProductCover
                        onMouseEnter={onShowDetails}
                        onMouseLeave={hideDetails}
                        minSizeInPx={288}
                        product={product}
                    />
                </Link>
                <SelectButton
                    productSelection={productSelection}
                    onClick={onSelectProduct}
                    isProductSelectable={isProductSelectable}
                    isProductSelected={isProductSelected}
                />
                <PreviewButton product={product} />
            </DisableWrapper>
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        <DisableWrapper className="tc tcw-25" isDisabled={isDisabled}>
                            <div>
                                <ProductSeries
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <p title={combinedTitle} className="label">
                                    <Link to={productLink}>
                                        <strong>{Utils.truncate(combinedTitle)}</strong>
                                    </Link>
                                </p>
                                <ProductAuthors
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductNarrators
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                            </div>
                        </DisableWrapper>
                        <DisableWrapper className="tc tcw-20" isDisabled={isDisabled}>
                            <div>
                                <ProductFormat
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductGenres
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                                <ProductLanguage
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                            </div>
                        </DisableWrapper>
                        <DisableWrapper className="tc tcw-25" isDisabled={isDisabled}>
                            <div>
                                <p>{product.isbn13}</p>
                                <ProductReleaseDate product={product} />
                                <ProductPublisher
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductUsageConditions
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                            </div>
                        </DisableWrapper>
                        <DisableWrapper className="tc tcw-25" isDisabled={isDisabled}>
                            <ProductStatistics product={product} />
                        </DisableWrapper>
                        <div className="tc tcw-20 actions">{buildActions()}</div>
                    </div>
                </div>

                <DisableWrapper isDisabled={isDisabled}>
                    <div className="product-list-bottom">
                        <CenteredListBadges>
                            <ListBadges
                                product={product}
                                loanFormatKey={loanFormatKey}
                                showAll={showDetails}
                                shopLanguage={undefined}
                            />
                        </CenteredListBadges>
                    </div>
                </DisableWrapper>
            </div>
        </div>
    );
};

export default CollectionListItem;
